import { calculateHandicap, parseHandicapMax } from "../../shared/calculations/scoreCalculations";
import {
  DEFAULT_GAME_ORDER,
  PAYOUT_CHOP,
  PAYOUT_FIRST,
  PAYOUT_REFUND,
  PAYOUT_SECOND,
  SCORING_HANDICAP,
  TIE_RULE_USBC_WC,
  roundMoney,
} from "./constants";

const gameAt = (order, round) => {
  const list = Array.isArray(order) && order.length ? order : DEFAULT_GAME_ORDER;
  return list[round - 1] || round;
};

const ticketScore = (ticket, round, context) => {
  if (!ticket) return null;
  const gameNumber = gameAt(context.gameOrder, round);
  const scratch = context.scoreByBowlerGame.get(
    `${ticket.bowlerID}:${gameNumber}`
  );
  if (scratch == null || scratch === "") return null;
  const pins = Number(scratch);
  if (!Number.isFinite(pins)) return null;
  if (context.scoringMode !== SCORING_HANDICAP) return pins;
  const hdcp = context.handicapByBowler.get(ticket.bowlerID) || 0;
  return pins + hdcp;
};

export const handicapForBowler = (averageRow, tournament) =>
  calculateHandicap(
    Number(averageRow?.average) || 0,
    Number(tournament?.handicapBase) || 0,
    Number(tournament?.handicapPercentage) || 0,
    parseHandicapMax(tournament?.handicapMax),
    averageRow?.hasAverage !== false
  );

const compareTickets = (left, right, round, context) => {
  const leftScore = ticketScore(left, round, context);
  const rightScore = ticketScore(right, round, context);
  if (leftScore == null || rightScore == null) {
    return { status: "pending", leftScore, rightScore };
  }
  if (leftScore > rightScore) {
    return { status: "left", leftScore, rightScore };
  }
  if (rightScore > leftScore) {
    return { status: "right", leftScore, rightScore };
  }
  return { status: "tie", leftScore, rightScore };
};

const breakTie = (left, right, round, context) => {
  const rule = context.tieRule || TIE_RULE_USBC_WC;
  if (rule !== TIE_RULE_USBC_WC) {
    return { status: "tie" };
  }
  if (round === 1) {
    return { status: "both" };
  }
  if (round === 2) {
    const leftG3 = ticketScore(left, 3, context);
    const rightG3 = ticketScore(right, 3, context);
    if (leftG3 == null || rightG3 == null) {
      return { status: "pending" };
    }
    if (leftG3 > rightG3) return { status: "left" };
    if (rightG3 > leftG3) return { status: "right" };
    return { status: "chop" };
  }
  return { status: "chop" };
};

const resolvePair = (left, right, round, context) => {
  if (!left && !right) {
    return { winner: null, advancers: [], status: "empty" };
  }
  if (left && !right) {
    return {
      winner: left,
      advancers: [left],
      status: "bye",
      leftScore: ticketScore(left, round, context),
      rightScore: null,
    };
  }
  if (!left && right) {
    return {
      winner: right,
      advancers: [right],
      status: "bye",
      leftScore: null,
      rightScore: ticketScore(right, round, context),
    };
  }
  const compared = compareTickets(left, right, round, context);
  if (compared.status === "pending") {
    return { winner: null, advancers: [], ...compared };
  }
  if (compared.status === "left") {
    return { winner: left, advancers: [left], ...compared };
  }
  if (compared.status === "right") {
    return { winner: right, advancers: [right], ...compared };
  }
  const tied = breakTie(left, right, round, context);
  if (tied.status === "both") {
    return { winner: null, advancers: [left, right], ...compared, status: "both" };
  }
  if (tied.status === "left") {
    return { winner: left, advancers: [left], ...compared, status: "tie-break-left" };
  }
  if (tied.status === "right") {
    return { winner: right, advancers: [right], ...compared, status: "tie-break-right" };
  }
  if (tied.status === "pending") {
    return { winner: null, advancers: [], ...compared, status: "pending" };
  }
  return {
    winner: null,
    advancers: [left, right],
    ...compared,
    status: "chop",
  };
};

const pickHighest = (tickets, round, context) => {
  const scored = tickets.map((ticket) => ({
    ticket,
    score: ticketScore(ticket, round, context),
  }));
  const ticketScores = Object.fromEntries(
    scored.map((row) => [row.ticket.id, row.score])
  );
  if (scored.some((row) => row.score == null)) {
    return { winner: null, advancers: [], status: "pending", ticketScores };
  }
  const best = Math.max(...scored.map((row) => row.score));
  const winners = scored.filter((row) => row.score === best).map((row) => row.ticket);
  if (winners.length === 1) {
    return { winner: winners[0], advancers: winners, status: "left", ticketScores };
  }
  if (round === 1) {
    return { winner: null, advancers: winners, status: "both", ticketScores };
  }
  if (round === 2) {
    return { ...pickHighest(winners, 3, context), ticketScores };
  }
  return { winner: null, advancers: winners, status: "chop", ticketScores };
};

const resolveMatchup = (tickets, round, context) => {
  const present = tickets.filter(Boolean);
  if (present.length <= 2) {
    return resolvePair(present[0] || null, present[1] || null, round, context);
  }
  return pickHighest(present, round, context);
};

const ticketsById = (tickets) => {
  const map = new Map();
  (tickets || []).forEach((ticket) => map.set(ticket.id, ticket));
  return map;
};

export const resolveTree = (tree, tickets, matches, context) => {
  const byId = ticketsById(tickets);
  const round1 = (matches || [])
    .filter((match) => match.treeID === tree.id && match.round === 1)
    .sort((a, b) => a.slot - b.slot);

  const resolvedRound1 = [0, 1, 2, 3].map((slot) => {
    const match = round1.find((row) => row.slot === slot);
    const left = match?.leftTicketID ? byId.get(match.leftTicketID) : null;
    const right = match?.rightTicketID ? byId.get(match.rightTicketID) : null;
    return {
      round: 1,
      slot,
      left,
      right,
      extra: [],
      ...resolveMatchup([left, right], 1, context),
    };
  });

  const round2Left = [
    ...(resolvedRound1[0].advancers || []),
    ...(resolvedRound1[1].advancers || []),
  ];
  const round2Right = [
    ...(resolvedRound1[2].advancers || []),
    ...(resolvedRound1[3].advancers || []),
  ];
  const round1Pending = resolvedRound1.some((match) => match.status === "pending");

  const resolvedRound2 = [
    {
      round: 2,
      slot: 0,
      left: round2Left[0] || null,
      right: round2Left[1] || null,
      extra: round2Left.slice(2),
      ...(round1Pending
        ? { winner: null, advancers: [], status: "pending" }
        : resolveMatchup(round2Left, 2, context)),
    },
    {
      round: 2,
      slot: 1,
      left: round2Right[0] || null,
      right: round2Right[1] || null,
      extra: round2Right.slice(2),
      ...(round1Pending
        ? { winner: null, advancers: [], status: "pending" }
        : resolveMatchup(round2Right, 2, context)),
    },
  ];

  const finalists = [
    ...(resolvedRound2[0].advancers || []),
    ...(resolvedRound2[1].advancers || []),
  ];
  const round2Pending = resolvedRound2.some((match) => match.status === "pending");
  const finalMatch = {
    round: 3,
    slot: 0,
    left: finalists[0] || null,
    right: finalists[1] || null,
    extra: finalists.slice(2),
    ...(round1Pending || round2Pending
      ? { winner: null, advancers: [], status: "pending" }
      : resolveMatchup(finalists, 3, context)),
  };

  const chopped = finalMatch.status === "chop";
  const first = chopped ? null : finalMatch.winner || null;
  const second =
    chopped || !first
      ? null
      : finalists.find((ticket) => ticket && ticket.id !== first.id) ||
        finalMatch.right ||
        finalMatch.left;

  return {
    tree,
    round1: resolvedRound1,
    round2: resolvedRound2,
    final: finalMatch,
    first,
    second,
    chopped,
    complete: Boolean(first) || chopped,
  };
};

export const aliveTicketIds = (resolved) => {
  const ids = new Set();
  if (!resolved) return ids;
  const add = (tickets) => {
    (tickets || []).forEach((ticket) => {
      if (ticket?.id) ids.add(ticket.id);
    });
  };
  if (!resolved.complete) {
    resolved.round1.forEach((match) => {
      if (match.status === "pending") {
        add([match.left, match.right, ...(match.extra || [])]);
      } else {
        add(match.advancers);
      }
    });
    resolved.round2.forEach((match) => {
      if (match.status === "pending") {
        add([match.left, match.right, ...(match.extra || [])]);
      } else {
        add(match.advancers);
      }
    });
    if (resolved.final.status === "pending") {
      add([
        resolved.final.left,
        resolved.final.right,
        ...(resolved.final.extra || []),
      ]);
    } else {
      add(resolved.final.advancers);
    }
  } else if (resolved.chopped) {
    add(resolved.final.advancers);
  } else if (resolved.first) {
    ids.add(resolved.first.id);
  }
  return ids;
};

export const owedForProduct = (product, tickets, resolvedTrees, payouts) => {
  const paidByKey = new Map();
  (payouts || []).forEach((row) => {
    paidByKey.set(`${row.kind}:${row.ticketID}`, row);
  });
  const rows = [];

  const enrich = (row, ticket, tree) => ({
    ...row,
    squadID: ticket?.squadID || tree?.squadID || null,
    treeNumber: tree?.treeNumber ?? null,
  });

  (tickets || [])
    .filter((ticket) => ticket.isLeftover)
    .forEach((ticket) => {
      const existing = paidByKey.get(`${PAYOUT_REFUND}:${ticket.id}`);
      rows.push(
        enrich(
          {
            kind: PAYOUT_REFUND,
            ticket,
            treeID: null,
            amount: Number(existing?.amount ?? product.entryAmount),
            paidAt: existing?.paidAt || null,
            payoutId: existing?.id || null,
          },
          ticket,
          null
        )
      );
    });

  (resolvedTrees || []).forEach((resolved) => {
    if (resolved.chopped) {
      const pot = roundMoney(
        Number(product.firstPlaceAmount) + Number(product.secondPlaceAmount)
      );
      const share = roundMoney(pot / (resolved.final.advancers || []).length);
      (resolved.final.advancers || []).forEach((ticket) => {
        const existing = paidByKey.get(`${PAYOUT_CHOP}:${ticket.id}`);
        rows.push(
          enrich(
            {
              kind: PAYOUT_CHOP,
              ticket,
              treeID: resolved.tree.id,
              amount: Number(existing?.amount ?? share),
              paidAt: existing?.paidAt || null,
              payoutId: existing?.id || null,
            },
            ticket,
            resolved.tree
          )
        );
      });
      return;
    }
    if (resolved.first) {
      const existing = paidByKey.get(`${PAYOUT_FIRST}:${resolved.first.id}`);
      rows.push(
        enrich(
          {
            kind: PAYOUT_FIRST,
            ticket: resolved.first,
            treeID: resolved.tree.id,
            amount: Number(existing?.amount ?? product.firstPlaceAmount),
            paidAt: existing?.paidAt || null,
            payoutId: existing?.id || null,
          },
          resolved.first,
          resolved.tree
        )
      );
    }
    if (resolved.second) {
      const existing = paidByKey.get(`${PAYOUT_SECOND}:${resolved.second.id}`);
      rows.push(
        enrich(
          {
            kind: PAYOUT_SECOND,
            ticket: resolved.second,
            treeID: resolved.tree.id,
            amount: Number(existing?.amount ?? product.secondPlaceAmount),
            paidAt: existing?.paidAt || null,
            payoutId: existing?.id || null,
          },
          resolved.second,
          resolved.tree
        )
      );
    }
  });

  return rows;
};

export const collectedAmount = (product, ticketCount) =>
  roundMoney((Number(product?.entryAmount) || 0) * (Number(ticketCount) || 0));

export const buildScoreLookup = (scores) => {
  const map = new Map();
  (scores || []).forEach((row) => {
    if (row?.bowlerID == null || row?.gameNumber == null) return;
    if (row.score == null || row.score === "") return;
    map.set(`${row.bowlerID}:${row.gameNumber}`, row.score);
  });
  return map;
};

export const buildHandicapLookup = (averages, tournament) => {
  const map = new Map();
  (averages || []).forEach((row) => {
    if (!row?.bowlerID) return;
    map.set(row.bowlerID, handicapForBowler(row, tournament));
  });
  return map;
};

export const resolveContext = ({
  product,
  tournament,
  scores,
  averages,
}) => ({
  gameOrder: product?.gameOrder || DEFAULT_GAME_ORDER,
  scoringMode: product?.scoringMode,
  tieRule: product?.tieRule || TIE_RULE_USBC_WC,
  scoreByBowlerGame: buildScoreLookup(scores),
  handicapByBowler: buildHandicapLookup(averages, tournament),
});

